import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getColor } from "../../utils/colorfunctions";
import { mixDisplayName } from "../../utils/mixDisplayName";
import "./PublicCompliance.css";

// plain axios instance — no auth interceptor, safe for public pages
const publicClient = axios.create({ baseURL: "" }); 
 
const PHYSICIAN_URL = "https://app.acuityscheduling.com/schedule/1c6e88df/appointment/96821840/calendar/5826382?appointmentTypeIds[]=96821840";

// --------------------------------------------------------------------------
// LicenseHeader — shows logo, company name, address, badge, license number
// ---------------------------------------------------------------------------
function LicenseHeader({ license }) {
    return (
        <header className="pc-header">
            <div className="pc-header__inner">
                {license.logo && (  
                    <img  
                        className="pc-header__logo"
                        src={license.logo}
                        alt={`${license.company} logo`}
                    />
                )}
                <div className="pc-header__info">
                    <div className="pc-header__company">{license.company}</div>
                    <div className="pc-header__badge">
                        Compliance Menu
                    </div>
                </div> 
            </div> 
        </header>  
    ); 
}

// ---------------------------------------------------------------------------
// FilterBar
// ---------------------------------------------------------------------------
const TYPE_LABELS = {
    flower: "Flower",
    "pre-roll": "Pre-Roll",
    "mix-flower": "Mixed Flower",
    "mix-preroll": "Mixed Pre-Roll",
};

function FilterBar({ search, onSearch, filterColor, onFilterColor, filterProductType, onFilterProductType, onClearSearch, colors, productTypes, showFilters }) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const hasActiveFilter = !!(filterColor || filterProductType);

    return (
        <div className="pc-filter-bar">
            <input
                className="pc-filter-bar__search"
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => onSearch(e.target.value)}
            />
            {showFilters && (
                <button
                    className={`pc-filter-bar__toggle${hasActiveFilter ? " pc-filter-bar__toggle--active" : ""}`}
                    onClick={() => setFiltersOpen((o) => !o)}
                >
                    Filters{hasActiveFilter ? " ●" : ""}
                </button>
            )}
            {(search || hasActiveFilter) && (
                <button
                    className="pc-filter-bar__clear"
                    onClick={() => { onClearSearch(); onFilterColor(""); onFilterProductType(""); }}
                >
                    Clear
                </button>
            )}
            {showFilters && filtersOpen && (
                <div className="pc-filter-bar__dropdowns">
                    <select
                        className="pc-filter-bar__select"
                        value={filterColor}
                        onChange={(e) => onFilterColor(e.target.value)}
                    >
                        <option value="">All Colors</option>
                        {colors.map((c) => (
                            <option key={c} value={c}>
                                {c.charAt(0).toUpperCase() + c.slice(1)}
                            </option>
                        ))}
                    </select>
                    <select
                        className="pc-filter-bar__select"
                        value={filterProductType}
                        onChange={(e) => onFilterProductType(e.target.value)}
                    >
                        <option value="">All Types</option>
                        {productTypes.map((t) => (
                            <option key={t} value={t}>
                                {TYPE_LABELS[t] || t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// MixCard — compliance card for mix products (multi-color, links to MixInfoPage)
// ---------------------------------------------------------------------------
function MixCard({ mix, company, licenseNo, companySlug }) {
    const navigate = useNavigate();
    const components = (() => {
        try { return typeof mix.components === "string" ? JSON.parse(mix.components) : (mix.components || []); }
        catch { return []; }
    })();
    const colors = [...new Set(components.map((c) => (c.color || "").toLowerCase()).filter(Boolean))];
    const typeLabel = "Mix";

    const firstCoaId = components.find((c) => c.coaId)?.coaId;

    return (
        <div className="pc-coa-card">
            <div className="pc-coa-card__header">
                <div className="pc-coa-card__title-row">
                    <img className="pc-coa-card__badge-icon" src="/DYC-icon-ColorWheel.png" alt="DYC" />
                    <div className="pc-coa-card__name-banner pc-coa-card__name-banner--mix">
                        <h3 className="pc-coa-card__name">{mixDisplayName(mix.product_name, mix.product_type, mix.created_at)}</h3>
                    </div>
                </div>
                <span className="pc-coa-card__dyc-id">{typeLabel}</span>
            </div>

            <div className="pc-mix-strains">
                {components.map((c, i) => (
                    <span key={i} className="pc-mix-strain">
                        <span className="pc-mix-strain__dot" style={{ backgroundColor: getColor((c.color || "").toLowerCase()) }} />
                        {c.productName}
                        {c.ratioPct != null && <span className="pc-mix-strain__pct"> ({parseFloat(c.ratioPct).toFixed(0)}%)</span>}
                    </span>
                ))}
            </div>

            <div className="pc-card-actions">
                <button className="pc-card-actions__btn pc-card-actions__btn--full" onClick={() => navigate(`/compliance/${companySlug}/mix/${mix.product_id}`)}>
                    View Product Info
                </button>
                <a className="pc-card-actions__btn pc-card-actions__btn--physician"
                    href={PHYSICIAN_URL} target="_blank" rel="noopener noreferrer">
                    Speak with a Physician
                </a>
                {firstCoaId && (
                    <>
                        <button className="pc-card-actions__btn pc-card-actions__btn--report pc-card-actions__btn--full"
                            onClick={() => navigate(`/compliance/${companySlug}/report/${firstCoaId}`, {
                                state: {
                                    coa: { coa_id: firstCoaId, product_name: mix.product_name, dycId: "" },
                                    company,
                                    licenseNo,
                                },
                            })}>
                            Report a Problem
                        </button>
                        <button className="pc-card-actions__btn pc-card-actions__btn--leo pc-card-actions__btn--full"
                            onClick={() => navigate("/compliance/leo")}>
                            Law Enforcement
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// PreRollCard — compliance card for pre-roll products (shares COA data with source flower)
// ---------------------------------------------------------------------------
function PreRollCard({ pr, company, licenseNo, companySlug }) {
    const navigate = useNavigate();
    const colorKey = (pr.color || "").toLowerCase();
    const accentColor = getColor(colorKey);
    const needsLightText = colorKey === "navy" || colorKey === "purple" || colorKey === "synthetic";

    const isPass = (v) => typeof v === "string" && v.toLowerCase().includes("pass");
    const isFail = (v) => typeof v === "string" && v.toLowerCase().includes("fail");

    const safetyFields = [pr.microbials, pr.pesticides, pr.heavy_metals, pr.mycotoxins];
    const allSafetyPass = safetyFields.every(isPass);
    const anySafetyFail = safetyFields.some(isFail);

    let contaminantStatus = "neutral";
    let contaminantLabel = "Not Tested";
    if (allSafetyPass) {
        contaminantStatus = "pass";
        contaminantLabel = "Pass";
    } else if (anySafetyFail) {
        contaminantStatus = "fail";
        contaminantLabel = "Fail";
    }

    const d9 = pr.delta_9_THC != null ? parseFloat(pr.delta_9_THC) : null;
    let thcStatus = "neutral";
    let thcLabel = "Not Tested";
    if (d9 !== null && !isNaN(d9) && d9 > 0) {
        if (d9 <= 0.3) {
            thcStatus = "pass";
            thcLabel = "Delta-9 Below 0.3%";
        } else {
            thcStatus = "fail";
            thcLabel = "Delta-9 Above 0.3%";
        }
    } else if (pr.has_cannabinoids) {
        thcStatus = "pass";
        thcLabel = "Passed THC Test";
    }

    return (
        <div className="pc-coa-card">
            <div className="pc-coa-card__header">
                <div className="pc-coa-card__title-row">
                    <img className="pc-coa-card__badge-icon" src="/DYC-icon-ColorWheel.png" alt="DYC" />
                    <div
                        className="pc-coa-card__name-banner"
                        style={accentColor ? { backgroundColor: accentColor, color: needsLightText ? "#ffffff" : undefined } : undefined}
                    >
                        <h3 className="pc-coa-card__name">{pr.product_name}</h3>
                    </div>
                </div>
                {pr.dycId && (
                    <span className="pc-coa-card__dyc-id">{pr.dycId}</span>
                )}
            </div>

            <div className="pc-compliance-rows">
                <div className={`pc-compliance-row pc-compliance-row--${contaminantStatus}`}>
                    <span className={`pc-compliance-row__icon pc-compliance-row__icon--${contaminantStatus}`}>
                        {contaminantStatus === "pass" ? "✓" : contaminantStatus === "fail" ? "✗" : "—"}
                    </span>
                    <span className="pc-compliance-row__label">Contaminants</span>
                    <span className={`pc-compliance-row__status pc-compliance-row__status--${contaminantStatus}`}>
                        {contaminantLabel}
                    </span>
                </div>
                <div className={`pc-compliance-row pc-compliance-row--${thcStatus}`}>
                    <span className={`pc-compliance-row__icon pc-compliance-row__icon--${thcStatus}`}>
                        {thcStatus === "pass" ? "✓" : thcStatus === "fail" ? "✗" : "—"}
                    </span>
                    <span className="pc-compliance-row__label">THC Content</span>
                    <span className={`pc-compliance-row__status pc-compliance-row__status--${thcStatus}`}>
                        {thcLabel}
                    </span>
                </div>
            </div>

            <div className="pc-card-actions">
                {pr.coa_id && (
                    <button className="pc-card-actions__btn pc-card-actions__btn--full" onClick={() => navigate(`/compliance/${companySlug}/product/${pr.coa_id}`, {
                        state: { coa: pr },
                    })}>
                        View Product Info
                    </button>
                )}
                <a className="pc-card-actions__btn pc-card-actions__btn--physician"
                    href={PHYSICIAN_URL} target="_blank" rel="noopener noreferrer">
                    Speak with a Physician
                </a>
                <button className="pc-card-actions__btn pc-card-actions__btn--report pc-card-actions__btn--full"
                    onClick={() => navigate(`/compliance/${companySlug}/report/${pr.coa_id}`, {
                        state: { coa: { coa_id: pr.coa_id, product_name: pr.product_name, dycId: pr.dycId || "" }, company, licenseNo },
                    })}>
                    Report a Problem
                </button>
                <button className="pc-card-actions__btn pc-card-actions__btn--leo pc-card-actions__btn--full"
                    onClick={() => navigate("/compliance/leo")}>
                    Law Enforcement
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// COACard — redesigned compliance card
// ---------------------------------------------------------------------------
function COACard({ coa, company, licenseNo, companySlug }) {
    const navigate = useNavigate();
    const pt = (coa.product_type || "").toLowerCase();
    const colorKey = (pt === "flower" || pt === "pre-roll") ? (coa.color || "").toLowerCase() : "no color";
    const accentColor = getColor(colorKey);
    const needsLightText = colorKey === "navy" || colorKey === "purple" || colorKey === "synthetic";

    const isPass = (v) => typeof v === "string" && v.toLowerCase().includes("pass");
    const isFail = (v) => typeof v === "string" && v.toLowerCase().includes("fail");

    const safetyFields = [coa.microbials, coa.pesticides, coa.heavy_metals, coa.mycotoxins];
    const allSafetyPass = safetyFields.every(isPass);
    const anySafetyFail = safetyFields.some(isFail);

    let contaminantStatus = "neutral";
    let contaminantLabel = "Not Tested";
    if (allSafetyPass) {
        contaminantStatus = "pass";
        contaminantLabel = "Pass";
    } else if (anySafetyFail) {
        contaminantStatus = "fail";
        contaminantLabel = "Fail";
    }

    const d9 = coa.delta_9_THC != null ? parseFloat(coa.delta_9_THC) : null;
    let thcStatus = "neutral";
    let thcLabel = "Not Tested";
    if (d9 !== null && !isNaN(d9) && d9 > 0) {
        if (d9 <= 0.3) {
            thcStatus = "pass";
            thcLabel = "Delta-9 Below 0.3%";
        } else {
            thcStatus = "fail";
            thcLabel = "Delta-9 Above 0.3%";
        }
    } else if (coa.has_cannabinoids) {
        thcStatus = "pass";
        thcLabel = "Passed THC Test";
    }

    return (
        <div className="pc-coa-card">
            <div className="pc-coa-card__header">
                <div className="pc-coa-card__title-row">
                    <img
                        className="pc-coa-card__badge-icon"
                        src="/DYC-icon-ColorWheel.png"
                        alt="DYC"
                    />
                    <div
                        className="pc-coa-card__name-banner"
                        style={accentColor ? { backgroundColor: accentColor, color: needsLightText ? "#ffffff" : undefined } : undefined}
                    >
                        <h3 className="pc-coa-card__name">{coa.product_name}</h3>
                    </div>
                </div>
                {coa.dycId && (
                    <span className="pc-coa-card__dyc-id">{coa.dycId}</span>
                )}
            </div>

            <div className="pc-compliance-rows">
                <div className={`pc-compliance-row pc-compliance-row--${contaminantStatus}`}>
                    <span className={`pc-compliance-row__icon pc-compliance-row__icon--${contaminantStatus}`}>
                        {contaminantStatus === "pass" ? "✓" : contaminantStatus === "fail" ? "✗" : "—"}
                    </span>
                    <span className="pc-compliance-row__label">Contaminants</span>
                    <span className={`pc-compliance-row__status pc-compliance-row__status--${contaminantStatus}`}>
                        {contaminantLabel}
                    </span>
                </div>
                <div className={`pc-compliance-row pc-compliance-row--${thcStatus}`}>
                    <span className={`pc-compliance-row__icon pc-compliance-row__icon--${thcStatus}`}>
                        {thcStatus === "pass" ? "✓" : thcStatus === "fail" ? "✗" : "—"}
                    </span>
                    <span className="pc-compliance-row__label">THC Content</span>
                    <span className={`pc-compliance-row__status pc-compliance-row__status--${thcStatus}`}>
                        {thcLabel}
                    </span>
                </div>
            </div>

            <div className="pc-card-actions">
                <button className="pc-card-actions__btn pc-card-actions__btn--full" onClick={() => navigate(`/compliance/${companySlug}/product/${coa.coa_id}`, {
                    state: { coa },
                })}>
                    View Product Info
                </button>
                <a className="pc-card-actions__btn pc-card-actions__btn--physician"
                    href={PHYSICIAN_URL} target="_blank" rel="noopener noreferrer">
                    Speak with a Physician
                </a>
                <button className="pc-card-actions__btn pc-card-actions__btn--report pc-card-actions__btn--full"
                    onClick={() => navigate(`/compliance/${companySlug}/report/${coa.coa_id}`, {
                        state: { coa, company, licenseNo },
                    })}>
                    Report a Problem
                </button>
                <button className="pc-card-actions__btn pc-card-actions__btn--leo pc-card-actions__btn--full"
                    onClick={() => navigate("/compliance/leo")}>
                    Law Enforcement
                </button>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function PublicCompliance() {
    const { companySlug } = useParams();
    const [searchParams] = useSearchParams();
    const [data, setData] = useState(null);   // { license, coas }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [filterColor, setFilterColor] = useState(searchParams.get("color") || "");
    const [filterProductType, setFilterProductType] = useState("");
    const filterProduct = searchParams.get("product") || "";
    const filterType = searchParams.get("type") || "";

    useEffect(() => {
        document.title = "DYC Compliance";
        return () => { document.title = "DYC Terminal"; };
    }, []);

    useEffect(() => {
        if (!companySlug) return;
        setLoading(true);
        setError(null);
        publicClient
            .get(`/public/compliance/${companySlug}`)
            .then((res) => setData(res.data))
            .catch((err) => {
                console.error("PublicCompliance fetch error:", err);
                setError(
                    err.response?.status === 404
                        ? "No compliance menu found for this license."
                        : "Something went wrong. Please try again later."
                );
            })
            .finally(() => setLoading(false));
    }, [companySlug]);

    const colors = useMemo(() => {
        const set = new Set();
        (data?.coas || []).forEach((c) => { if (c.color) set.add(c.color.toLowerCase()); });
        (data?.prerolls || []).forEach((p) => { if (p.color) set.add(p.color.toLowerCase()); });
        return [...set];
    }, [data]);

    const productTypes = useMemo(() => {
        const types = new Set();
        (data?.coas || []).forEach((c) => { if (c.product_type) types.add(c.product_type.toLowerCase()); });
        (data?.mixes || []).forEach((m) => { if (m.product_type) types.add(m.product_type.toLowerCase()); });
        (data?.prerolls || []).forEach((p) => { if (p.product_type) types.add(p.product_type.toLowerCase()); });
        return [...types].sort();
    }, [data]);

    const displayCoas = useMemo(() => {
        if (!data?.coas) return [];
        if (filterType === "mix") return [];
        if (filterProduct) {
            return data.coas.filter((c) => String(c.product_id) === filterProduct);
        }
        return data.coas.filter(
            (c) =>
                (c.product_name || "").toLowerCase().includes(search.toLowerCase()) &&
                (!filterColor || (c.color || "").toLowerCase() === filterColor.toLowerCase()) &&
                (!filterProductType || (c.product_type || "").toLowerCase() === filterProductType)
        );
    }, [data, search, filterColor, filterProduct, filterType, filterProductType]);

    const displayMixes = useMemo(() => {
        if (!data?.mixes) return [];
        if (filterProduct) return [];
        if (filterColor) return [];
        return data.mixes.filter(
            (m) =>
                (m.product_name || "").toLowerCase().includes(search.toLowerCase()) &&
                (!filterProductType || (m.product_type || "").toLowerCase() === filterProductType)
        );
    }, [data, search, filterColor, filterProduct, filterProductType]);

    const displayPrerolls = useMemo(() => {
        if (!data?.prerolls) return [];
        if (filterType === "mix") return [];
        if (filterProduct) return [];
        return data.prerolls.filter(
            (p) =>
                (p.product_name || "").toLowerCase().includes(search.toLowerCase()) &&
                (!filterColor || (p.color || "").toLowerCase() === filterColor.toLowerCase()) &&
                (!filterProductType || (p.product_type || "").toLowerCase() === filterProductType)
        );
    }, [data, search, filterColor, filterProduct, filterProductType, filterType]);

    const totalItems = displayCoas.length + displayMixes.length + displayPrerolls.length;

    if (loading) {
        return (
            <div className="pc-page pc-page--loading">
                <div className="pc-spinner" aria-label="Loading..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="pc-page pc-page--error">
                <div className="pc-error-box">{error}</div>
            </div>
        );
    }

    return (
        <div className="pc-page">
            {data?.license && <LicenseHeader license={data.license} />}

            <main className="pc-main">
                <FilterBar
                    search={search}
                    onSearch={setSearch}
                    onClearSearch={() => setSearch("")}
                    filterColor={filterColor}
                    onFilterColor={setFilterColor}
                    filterProductType={filterProductType}
                    onFilterProductType={setFilterProductType}
                    colors={colors}
                    productTypes={productTypes}
                    showFilters={!filterProduct && !filterType && !searchParams.get("color")}
                />

                {totalItems === 0 ? (
                    <div className="pc-empty">No products match your filters.</div>
                ) : (
                    <div className="pc-coa-grid">
                        {displayMixes.map((mix) => (
                            <MixCard
                                key={`mix-${mix.product_id}`}
                                mix={mix}
                                company={data.license.company}
                                licenseNo={data.license.licenseNo}
                                companySlug={companySlug}
                            />
                        ))}
                        {displayPrerolls.map((pr) => (
                            <PreRollCard
                                key={`pr-${pr.product_id}`}
                                pr={pr}
                                company={data.license.company}
                                licenseNo={data.license.licenseNo}
                                companySlug={companySlug}
                            />
                        ))}
                        {displayCoas.map((coa) => (
                            <COACard
                                key={`${coa.coa_id}-${coa.product_id ?? "none"}`}
                                coa={coa}
                                company={data.license.company}
                                licenseNo={data.license.licenseNo}
                                companySlug={companySlug}
                            />
                        ))}
                    </div>
                )}
            </main>

            <footer className="pc-footer">
                <span>Powered by DYC &mdash; Designate Your Chemotype</span>
            </footer>
        </div>
    );
}
